import { create } from 'zustand';
import { DEFAULT_DEVICE } from '../utils/deviceSpecs';

export const TOOL_TYPES = {
  LOADER: 'loader',
  GRADIENT: 'gradient',
  CARD: 'card',
  ANIMATION: 'animation',
  ALERT: 'alert',
  PHOTO_PICKER: 'photoPicker',
  STORYBOARD_CONVERTER: 'storyboardToSwiftUI',
  JSON_TO_MODEL: 'jsonToModel',
  API_GENERATOR: 'apiGenerator',
  POSTMAN_IMPORTER: 'postmanImporter',
};

export const TOOL_CATEGORIES = {
  SWIFTUI: 'swiftui',
  SWIFT: 'swift',
};

// Tool metadata: category and preview availability
export const TOOL_METADATA = {
  [TOOL_TYPES.LOADER]: { category: TOOL_CATEGORIES.SWIFTUI, hasPreview: true },
  [TOOL_TYPES.GRADIENT]: { category: TOOL_CATEGORIES.SWIFTUI, hasPreview: true },
  [TOOL_TYPES.CARD]: { category: TOOL_CATEGORIES.SWIFTUI, hasPreview: true },
  [TOOL_TYPES.ANIMATION]: { category: TOOL_CATEGORIES.SWIFTUI, hasPreview: true },
  [TOOL_TYPES.ALERT]: { category: TOOL_CATEGORIES.SWIFTUI, hasPreview: true },
  [TOOL_TYPES.PHOTO_PICKER]: { category: TOOL_CATEGORIES.SWIFTUI, hasPreview: true },
  [TOOL_TYPES.STORYBOARD_CONVERTER]: { category: TOOL_CATEGORIES.SWIFTUI, hasPreview: true },
  [TOOL_TYPES.JSON_TO_MODEL]: { category: TOOL_CATEGORIES.SWIFT, hasPreview: false },
  [TOOL_TYPES.API_GENERATOR]: { category: TOOL_CATEGORIES.SWIFT, hasPreview: true },
  [TOOL_TYPES.POSTMAN_IMPORTER]: { category: TOOL_CATEGORIES.SWIFT, hasPreview: false },
};

// Helper functions
export const getToolCategory = (toolType) => {
  return TOOL_METADATA[toolType]?.category || TOOL_CATEGORIES.SWIFTUI;
};

export const hasToolPreview = (toolType) => {
  return TOOL_METADATA[toolType]?.hasPreview ?? true;
};

export const getToolsByCategory = (category) => {
  return Object.entries(TOOL_METADATA)
    .filter(([_, metadata]) => metadata.category === category)
    .map(([toolType]) => toolType);
};

const useToolStore = create((set, get) => ({
  // Current active tool
  activeTool: TOOL_TYPES.LOADER,
  
  // Set active tool
  setActiveTool: (tool) => set({ activeTool: tool }),
  
  // Selected device for preview
  selectedDevice: DEFAULT_DEVICE,
  setSelectedDevice: (device) => set({ selectedDevice: device }),
  
  // Tool configurations
  toolConfigs: {
    [TOOL_TYPES.LOADER]: {
      style: 'ring',
      size: 50,
      primaryColor: '#F26F29',
      secondaryColor: '#F03224',
      speed: 1.0,
      strokeWidth: 4,
      showBackground: true,
      backgroundOpacity: 0.3,
      // Dots loader
      dotCount: 3,
      minDotSize: 8,
      maxDotSize: 20,
      useGradient: false,
      // Pulse loader
      minSize: 30,
      maxSize: 50,
      // Bars loader
      barCount: 5,
      barWidth: 4,
      barHeight: 30,
      barSpacing: 4,
      // Wave loader
      waveCount: 4,
      waveHeight: 20,
      waveWidth: 6,
      // Spinner loader
      armCount: 8,
      armLength: 20,
      // Progress Ring loader
      progress: 0.7,
      // Skeleton loader
      skeletonLineCount: 3,
      skeletonLineHeight: 12,
      skeletonLineSpacing: 8,
      // Bounce loader
      dotSize: 12,
      bounceHeight: 20,
      // Rotate loader
      elementCount: 6,
      elementSize: 8,
      // Fade loader (reuses elementCount, elementSize)
      // Scale loader
      minScale: 0.5,
      maxScale: 1.0,
      // Ripple loader
      rippleCount: 3,
      rippleWidth: 2,
      // Orbit loader
      orbitCount: 3,
      orbitSize: 8,
      orbitRadius: 20,
      // Grid loader
      gridRows: 3,
      gridCols: 3,
      cellSize: 10,
      // Lines loader
      lineCount: 5,
      lineWidth: 3,
      lineLength: 30,
      // Shimmer loader
      width: 200,
      height: 20,
      shimmerColor: '#ffffff',
      // Stagger loader
      staggerDelay: 0.1,
      // Morph loader
      shapeCount: 4,
      // Particle loader
      particleCount: 12,
      particleSize: 4,
      // Code generation options
      includeHelperClasses: true,
    },
    [TOOL_TYPES.GRADIENT]: {
      colors: ['#F26F29', '#F03224'],
      mode: 'linear', // 'linear' | 'radial' | 'angular' | 'elliptical'
      direction: 'vertical',
      customAngle: 0, // For custom linear direction
      componentType: 'view', // 'button' | 'view'
      hasCornerRadius: false,
      cornerRadius: 0,
      shape: 'rectangle', // 'circle' | 'square' | 'rectangle' | 'roundedRectangle' | 'capsule'
      buttonTitle: 'Button',
      textColor: '#FFFFFF',
      fontName: 'system',
      fontSize: 16,
      fontWeight: 'regular', // 'regular' | 'medium' | 'semibold' | 'bold'
      sizingMode: 'fixed', // 'fixed' | 'autolayout'
      width: 200,
      height: 50,
      autolayoutType: 'fullWidth', // 'fullWidth' | 'fullHeight' | 'padding' | 'aspectRatio'
      padding: 16,
      aspectRatio: 1.0,
      // Gradient stops with locations
      useStops: false,
      stops: [
        {color: '#F26F29', location: 0.0},
        {color: '#F03224', location: 1.0}
      ],
      // Enhanced RadialGradient
      radialCenter: 'center', // Preset or 'custom'
      radialCenterX: 0.5,
      radialCenterY: 0.5,
      startRadius: 0,
      endRadius: 100,
      // Enhanced AngularGradient
      angularCenter: 'center',
      angularCenterX: 0.5,
      angularCenterY: 0.5,
      startAngle: 0,
      endAngle: 360,
      angle: 0, // Keep for backward compatibility
      // Gradient opacity
      gradientOpacity: 1.0,
      // Presets
      selectedPreset: null,
    },
    [TOOL_TYPES.CARD]: {
      mode: 'card', // 'card' | 'fullUI'
      cardStyle: {
        padding: 16,
        cornerRadius: 12,
        shadowRadius: 8,
        shadowOpacity: 0.2,
        backgroundColor: '#ffffff',
        backgroundType: 'solid', // 'solid' | 'gradient' | 'image'
        gradientColors: ['#F26F29', '#F03224'],
      },
      viewTree: [], // Array of view nodes
      selectedViewId: null, // Currently selected view for properties editing
    },
    [TOOL_TYPES.ANIMATION]: {
      type: 'fadeIn',
      duration: 0.5,
      delay: 0,
      curve: 'easeInOut',
      stagger: 0.1,
      direction: 'up',
      repeat: false,
      repeatCount: 1,
      applyTo: 'view',
      springResponse: 0.5,
      springDampingFraction: 0.6,
    },
    [TOOL_TYPES.ALERT]: {
      type: 'success',
      title: 'Success',
      message: 'Operation completed successfully',
      buttonCount: 1,
      primaryButtonText: 'OK',
      secondaryButtonText: 'Cancel',
      tertiaryButtonText: 'Delete',
      cornerRadius: 12,
      blurBackground: true,
      blurIntensity: 10,
      animationType: 'fade',
      animationDuration: 0.3,
      backgroundColor: '#ffffff',
      titleColor: '#000000',
      messageColor: '#666666',
      // New dynamic features
      customTypeColor: null,
      customIcon: null,
      showIcon: true,
      dismissible: true,
      autoDismiss: false,
      autoDismissDelay: 3,
      primaryButtonStyle: 'borderedProminent',
      secondaryButtonStyle: 'bordered',
      tertiaryButtonStyle: 'bordered',
      primaryButtonColor: null,
      // Button-specific properties (similar to Cards section)
      primaryButtonBackgroundColor: null,
      primaryButtonTextColor: null,
      primaryButtonPadding: null,
      primaryButtonCornerRadius: null,
      primaryButtonBorderWidth: null,
      primaryButtonBorderColor: null,
      primaryButtonShadowEnabled: null,
      primaryButtonShadowRadius: null,
      primaryButtonShadowOpacity: null,
      secondaryButtonColor: null,
      secondaryButtonBackgroundColor: null,
      secondaryButtonTextColor: null,
      secondaryButtonPadding: null,
      secondaryButtonCornerRadius: null,
      secondaryButtonBorderWidth: null,
      secondaryButtonBorderColor: null,
      secondaryButtonShadowEnabled: null,
      tertiaryButtonColor: null,
      tertiaryButtonBackgroundColor: null,
      tertiaryButtonTextColor: null,
      tertiaryButtonPadding: null,
      tertiaryButtonCornerRadius: null,
      tertiaryButtonBorderWidth: null,
      tertiaryButtonBorderColor: null,
      tertiaryButtonShadowEnabled: null,
      shadowEnabled: true,
      shadowRadius: 20,
      shadowOpacity: 0.1,
      padding: 24,
    },
    [TOOL_TYPES.PHOTO_PICKER]: {
      selectionMode: 'single',
      sourceType: 'library',
      cropShape: 'square',
      cropEnabled: false,
      maxSelectionCount: 5,
      allowsEditing: false,
      filterType: 'images',
      enableDocumentPicker: false,
      enableCamera: true,
      enableGallery: true,
      allowVideos: false,
    },
    [TOOL_TYPES.STORYBOARD_CONVERTER]: {
      storyboardContent: '',
    },
    [TOOL_TYPES.JSON_TO_MODEL]: {
      jsonInput: '',
      modelName: 'Model',
      useOptional: true,
      useMacros: false,
    },
    [TOOL_TYPES.API_GENERATOR]: {
      method: 'GET',
      url: 'https://api.example.com/endpoint',
      headers: [{ key: 'Content-Type', value: 'application/json' }],
      queryParams: [{ key: '', value: '' }],
      bodyType: 'json',
      bodyContent: '{\n  "key": "value"\n}',
      errorHandling: 'standard',
      useAsyncAwait: true,
      includeCombineWrapper: true,
      baseURL: 'https://api.example.com',
      pattern: 'singleton',
      timeout: 30,
      enableRetry: false,
      enableLogging: false,
      environment: 'production',
      apiResponse: null,
      apiError: null,
      isLoading: false,
    },
    [TOOL_TYPES.POSTMAN_IMPORTER]: {
      collectionFile: null,
      pattern: 'singleton',
      useAsyncAwait: true,
      includeCombineWrapper: true,
      timeout: 30,
      enableRetry: false,
      enableLogging: false,
    },
  },
  
  // Update tool configuration
  updateToolConfig: (toolType, config) => set((state) => ({
    toolConfigs: {
      ...state.toolConfigs,
      [toolType]: {
        ...state.toolConfigs[toolType],
        ...config,
      },
    },
  })),
  
  // Generated code
  generatedCode: '',
  
  // Set generated code
  setGeneratedCode: (code) => set({ generatedCode: code }),
  
  // Generated files (for storyboard converter)
  generatedFiles: [],
  
  // Set generated files
  setGeneratedFiles: (files) => set({ generatedFiles: files, selectedFileIndex: files.length > 0 ? 0 : 0 }),
  
  // Selected file index (for preview)
  selectedFileIndex: 0,
  
  // Set selected file index
  setSelectedFileIndex: (index) => set({ selectedFileIndex: index }),
  
  // Dual preview states
  previewInCodeWindow: false,
  previewFileIndex: null,
  
  // Set preview in code window
  setPreviewInCodeWindow: (enabled) => set({ previewInCodeWindow: enabled }),
  
  // Set preview file index for code window
  setPreviewFileIndex: (index) => set({ previewFileIndex: index }),
  
  // Loading and error states for AI generation
  isGeneratingCode: false,
  generationError: null,
  
  // Set generating code state
  setIsGeneratingCode: (isGenerating) => set({ isGeneratingCode: isGenerating }),
  
  // Set generation error
  setGenerationError: (error) => set({ generationError: error }),
  
  // API response state (for API Generator)
  apiResponse: null,
  apiError: null,
  isLoading: false,
  
  // Set API response
  setApiResponse: (response) => set({ apiResponse: response, apiError: null }),
  
  // Set API error
  setApiError: (error) => set({ apiError: error, apiResponse: null }),
  
  // Set loading state
  setIsLoading: (loading) => set({ isLoading: loading }),
  
  // Clear API response
  clearApiResponse: () => set({ apiResponse: null, apiError: null, isLoading: false }),
}));

export default useToolStore;

